import React from "react";

import { ArrowRightIcon, TableIcon } from "@/assets/icons";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { PrimitiveDisplayValue, TOOLTIP_TRIGGER_LENGTH } from "@/types/common";
import { DataSchemaType } from "@/types/dataset";
import { isObjectArrayValue, isPrimitiveArrayValue } from "@/utils/common";
import { parseFeatureLinkItems } from "@/utils/string";

import { twMerge } from "tailwind-merge";

import Tooltip from "../ui/tooltip";
import TooltipButton from "../ui/TooltipButton";
import FeatureInfoLinks from "./FeatureInfoLinks";
import FeatureInfoPhotos from "./FeatureInfoPhotos";
import { FeaturePhotoItem } from "./types";
import {
  getDisplayValue,
  isArrayOfObjectsString,
  isArrayOfPrimitivesString
} from "./utils";

interface FeatureInfoItemProps {
  attrKey: string;
  value:
    | string
    | number
    | boolean
    | (string | number | boolean)[]
    | null
    | undefined;
  dataType?: DataSchemaType;
  photos?: FeaturePhotoItem[];
  isPOIDataset: boolean;
  showSelectAttributeIcon?: boolean;
  onAttributeSelected?: (data: {
    key: string;
    value: unknown;
    dataType?: DataSchemaType;
  }) => void;
}
interface RenderAttributeContext {
  key: string;
  value: unknown;
  dataType?: DataSchemaType;
  photos?: FeaturePhotoItem[];
  isPOIEnhancementEnabled: boolean;
  formattedKey: string;
  displayValue: string;
  isTooltipDisabled: boolean;
  showSelectAttributeIcon?: boolean;
  isArrayOfObjectsString: (val: unknown) => val is string;
  isArrayOfPrimitivesString: (val: unknown) => val is string;
  onAttributeSelected?: FeatureInfoItemProps["onAttributeSelected"];
}

interface RenderAttribute {
  condition: (ctx: RenderAttributeContext) => boolean;
  render: (ctx: RenderAttributeContext) => React.ReactNode;
}

const arrayOfObjectsAttribute: RenderAttribute = {
  condition: (ctx) =>
    ctx.isPOIEnhancementEnabled &&
    (ctx.isArrayOfObjectsString(ctx.value) || isObjectArrayValue(ctx.value)) &&
    ctx.key !== "adm_area" &&
    ctx.key !== "Photos",

  render: (ctx) => {
    const parsedArray: Array<Record<string, unknown> | string> =
      typeof ctx.value === "string" ? JSON.parse(ctx.value) : ctx.value;

    return (
      <div className="flex flex-col gap-1" key={ctx.key}>
        <div className="text-dim-gray flex gap-2 items-center justify-between">
          {ctx.formattedKey}
        </div>
        <div className="flex flex-col gap-2 text-sm text-txtHeading">
          {parsedArray.map((item, itemIndex) => {
            if (typeof item === "string") {
              return (
                <div
                  key={`${ctx.key}-${itemIndex}`}
                  className="whitespace-pre-wrap"
                >
                  {item}
                </div>
              );
            }

            const objectKey =
              typeof item === "object" && item !== null && "id" in item
                ? `${ctx.key}-obj-${(item as { id: string | number }).id}`
                : `${ctx.key}-obj-${itemIndex}-${Object.values(item).join("-")}`;

            return (
              <div key={objectKey} className="flex flex-col gap-1">
                {Object.entries(item).map(([objKey, objValue]) => {
                  const displayObjKey = objKey
                    .split("_")
                    .map(
                      (word: string) =>
                        word.charAt(0).toUpperCase() + word.slice(1)
                    )
                    .join(" ");

                  let displayObjValue: React.ReactNode;
                  const strValue = String(objValue || "").trim();

                  if (!strValue) {
                    displayObjValue = "-";
                  } else if (objKey === "local_url" && strValue) {
                    displayObjValue = (
                      <a
                        href={strValue}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-500 hover:underline"
                        title={strValue}
                      >
                        {strValue}
                      </a>
                    );
                  } else {
                    displayObjValue = strValue;
                  }

                  return (
                    <div key={objKey} className="flex gap-2">
                      <span className="font-semibold text-dim-gray">
                        {displayObjKey}:
                      </span>
                      <span>{displayObjValue}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
};

const arrayOfPrimitivesAttribute: RenderAttribute = {
  condition: (ctx) =>
    ctx.isPOIEnhancementEnabled &&
    ctx.key !== "adm_area" &&
    ctx.key !== "Photos" &&
    (ctx.isArrayOfPrimitivesString(ctx.value) ||
      isPrimitiveArrayValue(ctx.value)),
  render: (ctx) => {
    const parsedArray =
      typeof ctx.value === "string"
        ? (JSON.parse(ctx.value) as PrimitiveDisplayValue[])
        : (ctx.value as PrimitiveDisplayValue[]);

    return (
      <div className="flex flex-col gap-1" key={ctx.key}>
        <span className="text-dim-gray">{ctx.formattedKey}</span>
        <div className="flex flex-col gap-2 text-sm text-txtHeading">
          {parsedArray.map((item, itemIndex) => (
            <div
              key={`${ctx.key}-${itemIndex}`}
              className="whitespace-pre-wrap"
            >
              {item === null || item === undefined ? "" : String(item)}
            </div>
          ))}
        </div>
      </div>
    );
  }
};

const photosAttribute: RenderAttribute = {
  condition: (ctx) =>
    ctx.isPOIEnhancementEnabled && ctx.dataType === DataSchemaType.PHOTOS,
  render: (ctx) => {
    const photos = ctx.photos;

    if (!photos?.length) return null;

    return (
      <FeatureInfoPhotos
        key={ctx.key}
        formattedKey={ctx.formattedKey}
        photos={photos}
      />
    );
  }
};

const linksAttribute: RenderAttribute = {
  condition: (ctx) =>
    ctx.isPOIEnhancementEnabled && ctx.dataType === DataSchemaType.LINKS,
  render: (ctx) => {
    const links = parseFeatureLinkItems(ctx.value as string);
    if (links.length === 0) return null;

    return (
      <FeatureInfoLinks
        key={ctx.key}
        formattedKey={ctx.formattedKey}
        links={links}
      />
    );
  }
};

const adminAreaAttribute: RenderAttribute = {
  condition: (ctx) => ctx.isPOIEnhancementEnabled && ctx.key === "adm_area",
  render: (ctx) => {
    const values = Array.isArray(ctx.value) ? ctx.value : [];

    return (
      <div className="flex flex-col gap-1" key={ctx.key}>
        <span className="text-dim-gray">{ctx.formattedKey}</span>
        <div
          className="flex flex-wrap items-center gap-1 text-sm text-txtHeading"
          data-testid="adm-area-values"
        >
          {values.map((segment, index) => (
            <React.Fragment key={`${segment}-${index}`}>
              <span className="text-xs">{segment}</span>
              {index !== values.length - 1 && (
                <ArrowRightIcon
                  width={12}
                  height={12}
                  className="mx-0.5"
                  style={{ color: "#616161" }}
                  data-testid="adm-area-arrow"
                  aria-hidden="true"
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }
};

// static set of keys to exclude showing the table icon
const excludedKeysShowTableIcon = new Set([
  "Score",
  "Grid ID",
  "Catchment ID",
  "Geometry Source",
  "Rank"
]);

const defaultAttribute: RenderAttribute = {
  condition: () => true, // Always matches as fallback

  render: (ctx) => {
    const showTabularIcon =
      !!ctx.showSelectAttributeIcon &&
      !excludedKeysShowTableIcon.has(ctx.formattedKey);

    return (
      <div className={`flex flex-col gap-1`} key={ctx.key}>
        <div className="text-dim-gray flex gap-2 items-center justify-between">
          {ctx.formattedKey}
          {showTabularIcon && (
            <TooltipButton
              tooltipClassName="-mr-2"
              ariaLabel={`icon table ${ctx.formattedKey}`}
              position="top-right"
              content="Use this to filter the data by your selection."
              onClick={() =>
                ctx.onAttributeSelected?.({
                  key: ctx.key,
                  value: ctx.value,
                  dataType: ctx.dataType
                })
              }
            >
              <TableIcon
                aria-label="Use this to filter the data by your selection."
                className="fill-dim-gray"
              />
            </TooltipButton>
          )}
        </div>
        <Tooltip
          content={ctx.displayValue}
          position="bottom-center"
          className="z-[1000] text-left w-fit bg-neutral-800 max-w-screen-sm break-words whitespace-pre-wrap"
          arrowClassName="fill-neutral-800"
          disable={ctx.isTooltipDisabled}
        >
          <span
            className={twMerge(
              "text-txtHeading text-sm line-clamp-2 text-ellipsis",
              !ctx.isTooltipDisabled && "cursor-pointer"
            )}
            data-testid="feature-info"
          >
            {ctx.displayValue}
          </span>
        </Tooltip>
      </div>
    );
  }
};

// Order matters: handle specialized renderers (like photos/links) before generic JSON fallbacks
const renderAttributes: RenderAttribute[] = [
  photosAttribute,
  linksAttribute,
  arrayOfObjectsAttribute,
  arrayOfPrimitivesAttribute,
  adminAreaAttribute,
  defaultAttribute
];

export default function FeatureInfoItem(props: FeatureInfoItemProps) {
  const {
    value,
    attrKey: key,
    dataType,
    photos,
    isPOIDataset,
    showSelectAttributeIcon,
    onAttributeSelected
  } = props;
  const { isPOIInfoEnabled } = useAppFeatures();

  const isPOIEnhancementEnabled = isPOIInfoEnabled && isPOIDataset;

  const formattedKey = key
    .split("_")
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  const displayValue = getDisplayValue(value);
  const isTooltipDisabled = displayValue.length <= TOOLTIP_TRIGGER_LENGTH;

  // Build context for strategies
  const attributeContext: RenderAttributeContext = {
    key,
    value,
    dataType,
    photos,
    isPOIEnhancementEnabled,
    formattedKey,
    displayValue,
    isTooltipDisabled,
    showSelectAttributeIcon,
    isArrayOfObjectsString,
    isArrayOfPrimitivesString,
    onAttributeSelected
  };

  const atrribute = renderAttributes.find((s) => s.condition(attributeContext));
  return atrribute ? atrribute.render(attributeContext) : null;
}
